import React, { useState } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import CopyValueButton from "../components/CopyValueButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { formatDate } from "../utils/helpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import { certificateName } from "../../shared/utils/certificateNaming";

/**
 * Compact copyable result field for the SSL Certificate Details card.
 *
 * 2026-08-07 download/copy consistency pass (operator-directed) : this
 * page rendered the decoded SSL Certificate details (Common Name,
 * serial number, validity dates) with no copy affordance at all - the
 * only result card in the suite without one. Each populated field now
 * carries the compact label-row copy button with the state-driven
 * transient "Copied!" feedback ; the component mirrors the ResultField
 * component in src/client/pages/CSRDecoder.jsx (the per-field
 * reference), narrowed to this card's plain (uninset) field styling.
 * `mono` preserves the pre-existing font-mono treatment the Serial
 * Number value alone carried.
 */
function ResultField({
  label,
  value,
  display,
  fieldKey,
  copyStatus,
  onCopy,
  mono,
}) {
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-muted uppercase tracking-wider">
          {label}
        </span>
        {/* The copy button only renders for a real value : the "N/A"
            placeholder on an absent Common Name / Organization is
            explanatory text, not a copyable value. The gate is
            CopyValueButton's own falsy-value null return, so every
            consumer shares it. */}
        <CopyValueButton
          label={label}
          value={value}
          fieldKey={fieldKey}
          copyStatus={copyStatus}
          onCopy={onCopy}
        />
      </div>
      {/* `display` is the CSRDecoder ResultField value/display split :
          the clipboard gets the machine-usable `value` (a raw ISO
          date) while the screen shows the friendly form. */}
      <p className={`text-heading ${mono ? "font-mono" : ""}`}>
        {display || value || "N/A"}
      </p>
    </div>
  );
}

function CertificateMatcher() {
  const [certificate, setCertificate] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Transient per-field copy feedback : the shared success-gated
  // "Copied!" hook (see src/client/hooks/useCopyFeedback.js).
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!certificate.trim() || !privateKey.trim()) {
      setError("Please provide both the SSL Certificate and the private key.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    resetCopyStatus();

    try {
      const response = await api.verifyMatch(certificate, privateKey);
      setResult(response.data);
    } catch (err) {
      setError(
        err.message ||
          "The SSL Certificate and private key could not be compared."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCertificate("");
    setPrivateKey("");
    setResult(null);
    setError(null);
    resetCopyStatus();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/*
        2026-07-04 tools-wide overhaul (operator-approved), audit T2
        #5 sweep applied to meta text too : the title and keywords
        previously used bare "Certificate" and "SSL" alone ; every
        reference now reads "SSL Certificate" per the style guide,
        and the canonical URL is added (the shared SEO component only
        emits the canonical link when the prop is provided).
      */}
      <SEO
        title="SSL Certificate & Private Key Matcher - Verify SSL Certificate and Private Key Match"
        description="Verify if an SSL Certificate and private key match. Free online tool to check SSL Certificate and private key pairing. Validate that your SSL Certificate matches its corresponding private key."
        keywords="SSL Certificate key matcher, SSL Certificate matcher, verify SSL Certificate key match, Private Key matcher, SSL Certificate Private Key validation, SSL Certificate key pair checker, SSL Certificate key verification tool"
        canonicalUrl="https://tools.trustico.com/match"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          SSL Certificate & Private Key Matcher
        </h1>
        <p className="text-body">
          Confirm that an SSL Certificate and a private key belong together
          before you install them. The matcher checks the private key against
          the public key inside the SSL Certificate, and tells you whether the
          two form a matching pair. A mismatched pair is a common cause of a
          failed installation, especially after a reissue or a server move when
          several key files look alike. Run the check whenever more than one key
          file is on hand and the right one is unclear.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Certificate Input */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              SSL Certificate
            </h2>
            <p className="text-body mb-4">
              Paste your SSL Certificate in PEM format below.
            </p>
            <textarea
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              className="textarea certificate-textarea"
              rows={12}
            />
          </div>

          {/* Private Key Input */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Private Key
            </h2>
            <p className="text-body mb-4">
              Paste your private key in PEM format below. This should be the
              private key that was used to generate the CSR for the SSL
              Certificate above.
            </p>
            <textarea
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              className="textarea certificate-textarea"
              rows={12}
            />
          </div>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <ActionButton
              type="submit"
              variant="primary"
              disabled={loading || !certificate.trim() || !privateKey.trim()}
              loading={loading}
              loadingText="Verifying..."
              icon={ShieldCheckIcon}
            >
              Verify Match
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <div ref={resultsRef} className="mt-8 space-y-6 animate-fade-in">
          {/* Match Result */}
          <div
            className={`card p-8 text-center ${result.match ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800/50" : "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/50"}`}
          >
            {/*
              2026-07-04 tools-wide overhaul (operator-approved),
              audit T2 #7 : these result headings are status notices
              (the tool ran successfully in both branches), not error
              message text, so they carry Title Case rather than
              blanket UPPERCASE. Error Alerts on this page are plain
              sentences too since the 2026-08-27 ruling.

              2026-07-04 operator consistency directive, heading-size
              sweep decision : these status banners keep text-2xl
              font-bold DELIBERATELY. They are result-status banners
              (centered icon + colored verdict), not section headings,
              and the only other status banner in the suite (the "PFX
              File Generated Successfully" banner in PFXConverter.jsx)
              uses the identical treatment, so the two banner sites are
              already mutually consistent. Section headings follow the
               text-lg font-semibold reference size.
            */}
            {result.match ? (
              <>
                <CheckCircleIcon className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2">
                  SSL Certificate and Key Match
                </h2>
                <p className="text-green-700 dark:text-green-300">
                  The SSL Certificate and Private Key Form a Valid Matching Pair
                </p>
              </>
            ) : (
              <>
                <XCircleIcon className="w-16 h-16 text-red-600 dark:text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-800 dark:text-red-200 mb-2">
                  SSL Certificate and Key Do Not Match
                </h2>
                <p className="text-red-700 dark:text-red-300">
                  The SSL Certificate and Private Key Do Not Form a Matching
                  Pair
                </p>
              </>
            )}
          </div>

          {/* Certificate Details */}
          {result.certificate && (
            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                SSL Certificate Details
              </h2>
              {/* 2026-07-04 operator consistency directive : every
                  section heading carries an explanatory paragraph (the
                  CSRGenerator.jsx reference idiom). */}
              <p className="text-body mb-4">
                These details were decoded from the SSL Certificate you
                provided, so you can confirm the correct SSL Certificate was
                checked.
              </p>
              {/*
                2026-07-04 tools-wide overhaul (operator-approved),
                audit T2 #8 : this page was the only result card in
                the suite rendering a " :" after the small-caps
                labels ; the trailing colons were dropped to match
                every sibling result card (ChainAnalyzer,
                PFXConverter, CSRDecoder).
              */}
              {/* 2026-08-07 download/copy consistency pass
                  (operator-directed) : every value field carries the
                  compact copy button (see the ResultField JSDoc at the
                  top of this file). The Status field deliberately does
                  not : EXPIRED / VALID is a colored verdict badge, not
                  a value a customer transcribes anywhere. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ISSUED TO, AND READ FROM THE SHARED HELPER
                    (operator ruling, 2026-08-21). "Common Name" named
                    one field of the subject rather than the identity of
                    the SSL Certificate, so an S/MIME SSL Certificate -
                    which carries no common name at all - left this
                    field EMPTY while its e-mail address sat unread in
                    the Subject Alternative Names. Issued To is true of
                    every kind, and matches the Chain Analyzer and the
                    PFX Converter. */}
                {/* THE CLIPBOARD AND THE SCREEN GET THE SAME BARE NAME,
                    with no "+ N More SANs" count (operator ruling,
                    2026-08-21). The Chain Analyzer earns that suffix
                    because it lists every name in a section of its own ;
                    this page has no such section, so a count here would
                    leave a customer with a number, an abbreviation
                    nobody introduced, and nowhere to go.

                    AND A SECOND REASON NOT TO REACH FOR THE DISPLAY
                    FORM HERE, whatever the ruling says later : this
                    field is the one place the shared helper meets a
                    copy button, and ResultField's `value` is what
                    reaches the clipboard. The display form would put
                    "*.trustico.com + 59 More SANs" on the clipboard of
                    a customer about to paste a host name into a
                    configuration file or a Certificate Signing Request.
                    Screen and clipboard want the same value, so this
                    field needs no display override.

                    It also keeps the no-copy-button-on-a-placeholder
                    gate honest : certificateName returns null when
                    nothing names the SSL Certificate, so CopyValueButton
                    renders nothing and the "N/A" fallback still shows. */}
                <ResultField
                  label="Issued To"
                  value={certificateName(result.certificate)}
                  fieldKey="commonName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
                <ResultField
                  label="Organization"
                  value={result.certificate.subject.organizationName}
                  fieldKey="organizationName"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
                <ResultField
                  label="Serial Number"
                  value={result.certificate.serialNumber}
                  fieldKey="serialNumber"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                  mono
                />
                {/* The dates copy the raw ISO value (unambiguous
                    across locales in a support ticket or script)
                    while displaying the friendly locale form - the
                    same value/display split the CSRDecoder Key Size
                    field uses. */}
                <ResultField
                  label="Valid From"
                  value={result.certificate.notBefore}
                  display={formatDate(result.certificate.notBefore)}
                  fieldKey="notBefore"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
                <ResultField
                  label="Valid Until"
                  value={result.certificate.notAfter}
                  display={formatDate(result.certificate.notAfter)}
                  fieldKey="notAfter"
                  copyStatus={copyStatus}
                  onCopy={handleCopy}
                />
                <div>
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Status
                  </span>
                  <p
                    className={`font-medium ${result.certificate.isExpired ? "text-red-600 dark:text-red-300" : "text-green-700 dark:text-green-400"}`}
                  >
                    {result.certificate.isExpired ? "EXPIRED" : "VALID"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CertificateMatcher;
